// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Pagination dots — one button per `scrollSnap`. Highlights the active slide
 * and (when `clickable`) lets the user jump straight to any snap point.
 *
 * Reads carousel state from {@link CarouselContext}; render it anywhere below
 * a `CarouselProvider`.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import { useCarouselContext } from "@vanmeter/App/Components/XngCarousel/CarouselContext";
import { Dot } from "@vanmeter/App/Components/XngCarousel/styled";
import IconPause from "@vanmeter/XDL/Icons/IconPause";
import IconPlay from "@vanmeter/XDL/Icons/IconPlayBanner";
import React, { FC, useCallback } from "react";

/** Props for the {@link Pagination} component. */
export interface PaginationProps {
    /** Whether dots respond to click. Defaults to `true`. */
    clickable?: boolean;
    /** Extra class name appended to the dot list. */
    className?: string;
    /** Allow pause autoplay */
    pausable?: boolean;
}

export const Pagination: FC<PaginationProps> = ({ clickable = true, className, pausable }) => {
    const ctx = useCarouselContext();
    const carouselApi = ctx?.carouselApi ?? null;
    const scrollSnaps = ctx?.scrollSnaps ?? [];
    const selectedIndex = ctx?.selectedIndex ?? 0;

    const handleClick = useCallback(
        (index: number) => {
            if (!clickable) return;
            carouselApi?.scrollTo(index);
            // XNG-Changes: Reset the autoplay timer whenever a dot is clicked.
            carouselApi?.plugins().autoplay?.reset();
        },
        [carouselApi, clickable],
    );

    if (!carouselApi || scrollSnaps.length <= 1) return null;

    const classes = ["carousel-pagination", className].filter(Boolean).join(" ");

    return (
        <StyledWrapper className={classes}>
            {scrollSnaps.map((_, index) => {
                const isActive = index === selectedIndex;
                return (
                    <Clickable
                        key={index}
                        type="button"
                        className={`carousel-pagination-button${isActive ? " is-active" : ""}`}
                        onClick={() => handleClick(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={isActive ? "true" : undefined}
                    >
                        <Dot active={isActive} />
                    </Clickable>
                );
            })}
            {pausable && (
                <Clickable
                    key={"pause-play-btn"}
                    onClick={ctx?.onPlayPause}
                    className="pause-play-btn"
                    aria-label={translate(ctx?.paused ? "Play slideshow" : "Pause slideshow")}
                >
                    {ctx?.paused ? (
                        <Icon color="common.white" src={IconPlay} height={14} width={12} />
                    ) : (
                        <Icon color="common.white" src={IconPause} height={12} width={12} />
                    )}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

export default Pagination;
